import { Grid2 as Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { RateType as RateTypeEnum, UnitName as UnitNameEnum } from "@sinch/core/entities/serverEnums";
import { UnitName } from "@sinch/core/entities/serverEnums.types";
import { isEmpty, mergeDeepRight } from "ramda";
import { isNull } from "ramda-adjunct";
import React, { FC, forwardRef, type Ref, useCallback, useMemo } from "react";
import { CurrencyInput } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/CurrencyInput";
import { PercentageInput } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/PercentageInput";
import { RateBaseSelect } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/RateBaseSelect";
import { RateInputValue } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/RateInput";

interface ConditionInputInputsProps {
  onFocusChange: (index: number) => void;
  onChangeValue: (val: RateInputValue) => void;
  value: RateInputValue;
  unit?: string | null;
  rateName?: string | null;
  onBlur: () => void;
  readOnly?: boolean;
  unitName: UnitName | null;
  availableRateComponents?: string[];
}

const defaultRateValues = { baseComponent: null, amount: 0, type: RateTypeEnum.Simple };

export const RateInputInputs: FC<ConditionInputInputsProps> = forwardRef(
  (
    {
      onFocusChange,
      onChangeValue,
      value = defaultRateValues,
      onBlur,
      unit,
      rateName,
      readOnly,
      unitName,
      availableRateComponents,
    },
    // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    _: Ref<void>
  ) => {
    const isPercent = unitName === UnitNameEnum.Currency;
    const adornment = useMemo(() => {
      if (value.type === RateTypeEnum.Percentage || isPercent) {
        return "%";
      }
      if (isNull(unit)) {
        return "fix";
      }
      return unit ? `/${unit}` : "";
    }, [isPercent, unit, value.type]);

    const modifyVal = mergeDeepRight(mergeDeepRight(defaultRateValues, value ?? {}));

    const handleRateBaseChange = useCallback(
      (val: string | null) => {
        onChangeValue(
          modifyVal({
            baseComponent: isEmpty(val) ? null : val,
            type: val === "" ? RateTypeEnum.Simple : RateTypeEnum.Percentage,
            amount: 0,
          })
        );
      },
      [onChangeValue, modifyVal]
    );

    const handleRateBaseOpenChange = useCallback(
      (open: boolean) => {
        onFocusChange(open ? 1 : -1);
      },
      [onFocusChange]
    );

    const handleAmountChange = useCallback(
      (val: number | null) => {
        onChangeValue(
          modifyVal({
            amount: val,
          })
        );
      },
      [onChangeValue, modifyVal]
    );

    return (
      <FullDimensionGrid container direction="row">
        {!isNull(unit) && (
          <BorderedGrid size="grow">
            <RateBaseSelect
              availableRateComponents={availableRateComponents}
              onBlur={onBlur}
              onChange={handleRateBaseChange}
              onOpenChange={handleRateBaseOpenChange}
              rateName={rateName}
              readOnly={readOnly}
              unit={unit}
              value={value.baseComponent ?? ""}
            />
          </BorderedGrid>
        )}
        <Grid size="grow">
          {value.type === RateTypeEnum.Percentage || isPercent ? (
            <PercentageInput
              adornment={adornment}
              data-cy="RateInput-amount-percent"
              onBlur={onBlur}
              onChange={handleAmountChange}
              readOnly={readOnly}
              value={value.amount}
            />
          ) : (
            <CurrencyInput
              adornment={adornment}
              data-cy="RateInput-amount-unit"
              onBlur={onBlur}
              onChange={handleAmountChange}
              readOnly={readOnly}
              value={value.amount}
            />
          )}
        </Grid>
      </FullDimensionGrid>
    );
  }
);

RateInputInputs.displayName = "ConditionInputInputs";

const FullDimensionGrid = styled(Grid)({
  width: "100%",
  height: "100%",
});

const BorderedGrid = styled(Grid)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.grey[400]}`,
}));
